import { InstagramUser } from '@round/api';
import { Skeleton, TableProps, getTableMetaHelper } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useInstagramReportCampaignPosts, InstagramReportPostsTableRow } from './useInstagramReportCampaignPosts';
import CostCell from 'Modules/Plans/components/CostCell/CostCell';
import TotalCostFooterCell from 'Modules/Plans/components/TotalCostFooterCell/TotalCostFooterCell';
import { CampaignTableRow } from '../../Campaigns/CampaignsTable/ReportCampaignsTable';
import NotesCell from 'Modules/Plans/components/NotesCell/NotesCell';
import VirtualizedPostsTable from 'Modules/Plans/Posts/components/PostsTable/VirtualizedPostsTable';
import postsTableStyles from 'Modules/Plans/Posts/components/PostsTable/PostsTable.module.css';
import PostsTableStatusCell, {
    StatusData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/StatusCell/PostsTableStatusCell';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import PostStatsValueCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsValueCell';
import PostStatsFooterTotalCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableStatFooterCell';
import PostStatsViewsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostStatsViewsFooterCell';
import PostsTableAccountCell, {
    AccountData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableAccountCell/PostsTableAccountCell';
import PostsTableUploadedDateCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableUploadedDateCell';
import { getInstagramEngagementRate, getInstagramTotalEngagement } from 'Modules/Plans/Posts/helpers';
import PostsTableEngagementRateFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementRateFooterCell';
import PostsTableEngagementsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementsFooterCell';
import InstagramUserModal from 'Modules/Plans/User/UserModal/InstagramUserModal/InstagramUserModal';
import PostStatsWithAverageCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsWithAverageCell/PostsStatsWithAverageCell';
import PostStatsWithAverageFooterTotalCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsWithAverageCell/PostStatsWithAverageFooterCell';
import CreatorDetailsModal from 'Modules/Plans/CreatorSearch/components/CreatorDetailsModal/CreatorDetailsModal';
import { buildInstagramUserUrl } from 'helpers';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';
import { ModalConfig } from '../../../../CreatorSearch/components/CreatorDetailsModal/helpers';

type Props = Pick<TableProps<InstagramReportPostsTableRow>, 'noDataLabel'> & {
    campaign: CampaignTableRow;
};

type TableCellContext<TKey extends keyof InstagramReportPostsTableRow> = CellContext<
    InstagramReportPostsTableRow,
    InstagramReportPostsTableRow[TKey]
>;

type Meta = BasePostTableMeta<InstagramReportPostsTableRow> & {
    campaign: CampaignTableRow;
};
const getTableMeta = getTableMetaHelper<Meta>();

const InstagramReportPostsTable = ({ campaign }: Props) => {
    const {
        data,
        isLoading,
        hasError,
        getIsAccountDataLoading,
        postsTotals,
        areTotalsLoading,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useInstagramReportCampaignPosts(campaign.id);
    const [accountModalUserId, setAccountModalUserId] = useState<InstagramUser['id'] | null>(null);
    const [clientAccountModalConfig, setClientAccountModalConfig] = useState<ModalConfig | null>(null);

    const columns = useMemo<ColumnDef<InstagramReportPostsTableRow, any>[]>(
        () => [
            {
                header: 'ID',
                meta: { className: postsTableStyles.idColumn },
                accessorKey: 'id',
                cell: ({ getValue, table }: TableCellContext<'id'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return getValue().slice(-8);
                },
            },
            {
                header: 'Account',
                meta: { className: postsTableStyles.reportAccountColumn },
                accessorFn: (row): AccountData => ({
                    accountId: row.instagram_details?.account_id,
                    image: row.image?.avatar_thumb.cached_url ?? row.image?.avatar_thumb.original_url ?? '',
                    title: row.user?.username ? `@${row.user?.username}` : '-',
                    followerCount: row.userStatsCache?.follower_count || null,
                    profileUrl: row.user?.username ? buildInstagramUserUrl(row.user.username) : null,
                }),
                id: 'account',
                cell: PostsTableAccountCell,
            },
            {
                header: 'Status',
                meta: { className: postsTableStyles.statusColumn },
                id: 'status',
                accessorFn: (original): StatusData => ({
                    postUrl: original.post_url,
                    platform: original.platform,
                    adCode: original.instagram_details?.ad_code,
                    isReadOnly: true,
                }),
                cell: PostsTableStatusCell,
            },
            {
                header: 'Cost',
                meta: { className: postsTableStyles.costColumn },
                accessorKey: 'cost',
                cell: ({ getValue, table }: TableCellContext<'cost'>) => {
                    const { isLoading, campaign } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }
                    return <CostCell isReadonly cost={getValue()} currency={campaign?.currency ?? null} />;
                },
                footer: ({ table }) => {
                    const { campaign, postsTotals, areTotalsLoading } = getTableMeta(table);

                    if (areTotalsLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <TotalCostFooterCell
                            costs={table.getRowModel().rows.map((r) => r.original.cost)}
                            currency={campaign.currency}
                            total={postsTotals?.cost ?? null}
                        />
                    );
                },
            },
            {
                header: 'Uploaded date',
                meta: { className: postsTableStyles.uploadedDateColumn },
                id: 'uploadedDate',
                cell: PostsTableUploadedDateCell,
            },
            {
                header: 'Views',
                meta: { className: postsTableStyles.statColumn },
                id: 'views',
                accessorFn: (row) => ({
                    value: row.view_count,
                    average: row.userStatsCache?.avg_post_views,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostStatsViewsFooterCell,
            },
            {
                header: 'Story views',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (original) => original.instagram_details?.story_view_count,
                cell: PostStatsValueCell,
                footer: (context) => <PostStatsFooterTotalCell {...context} totalsField="story_view_count" />,
            },
            {
                header: 'Total engagements',
                meta: { className: postsTableStyles.totalEngagementsColumn },
                id: 'totalEngagements',
                accessorFn: (row) => ({
                    value: getInstagramTotalEngagement(row.instagram_details),
                    average: row.userStatsCache?.avg_post_total_engagements,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostsTableEngagementsFooterCell,
            },
            {
                header: () => <TooltipHeader header="ER" tooltipBody={tooltipCopy.engagementRate} />,
                meta: { className: postsTableStyles.statColumn },
                id: 'engagementRate',
                accessorFn: (row) => ({
                    value: getInstagramEngagementRate(row),
                    average: row.userStatsCache?.avg_engagement_rate,
                    formatValueToPercentage: true,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostsTableEngagementRateFooterCell,
            },
            {
                header: 'Likes',
                meta: { className: postsTableStyles.statColumn },
                id: 'likes',
                accessorFn: (row) => ({
                    value: row.instagram_details?.like_count,
                    average: row.userStatsCache?.avg_post_likes,
                }),
                cell: PostStatsWithAverageCell,
                footer: (context) => <PostStatsWithAverageFooterTotalCell {...context} totalsField="like_count" />,
            },
            {
                header: 'Comments',
                meta: { className: postsTableStyles.statColumn },
                accessorKey: 'stats',
                id: 'comments',
                accessorFn: (row) => ({
                    value: row.instagram_details?.comment_count,
                    average: row.userStatsCache?.avg_post_comments,
                }),
                cell: PostStatsWithAverageCell,
                footer: (context) => <PostStatsWithAverageFooterTotalCell {...context} totalsField="comment_count" />,
            },
            {
                header: 'Notes',
                meta: { className: postsTableStyles.notesColumn },
                accessorKey: 'notes',
                cell: ({ getValue, table }: TableCellContext<'notes'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }
                    return <NotesCell notes={getValue()} />;
                },
            },
        ],
        []
    );

    const meta: Meta = {
        isLoading,
        getIsAccountDataLoading,
        campaign,
        managedBy: campaign.managed_by,
        areApiTotalsEnabled: true,
        postsTotals,
        areTotalsLoading,
        setAccountModalUserId: setAccountModalUserId,
        onOpenClientAccountModal: setClientAccountModalConfig,
    };

    return (
        <>
            <VirtualizedPostsTable
                columns={columns}
                data={data}
                meta={meta}
                isLoading={isLoading}
                hasError={hasError}
                onEndReached={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
            />
            <InstagramUserModal
                key={accountModalUserId}
                isOpen={typeof accountModalUserId === 'number'}
                onClose={() => {
                    setAccountModalUserId(null);
                }}
                userId={accountModalUserId}
            />

            <CreatorDetailsModal
                key={clientAccountModalConfig?.clientInfluencerAccountId}
                isOpen={!!clientAccountModalConfig}
                onClose={() => setClientAccountModalConfig(null)}
                clientInfluencerAccountId={clientAccountModalConfig?.clientInfluencerAccountId}
            />
        </>
    );
};

export default InstagramReportPostsTable;
